import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, Skeleton, Tabs } from '../../../components/ui';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { money } from '../../../utils/currency';
import { MovementsCard } from '../components/MovementsCard/MovementsCard';
import { stockService } from '../services/stockService';
import type { ReactNode } from 'react';

function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <Card className="p-5">
      <p className="text-sm text-ink-500">{label}</p>
      <p className={cn('tabular mt-1 text-2xl font-semibold text-navy-900', tone)}>{value}</p>
    </Card>
  );
}

export default function StockList() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const { data, loading, error, reload } = useFetch(() => stockService.summary(), []);

  const rows = (data?.rows ?? []).filter(
    (r) => (tab === 'all' || r.isLow) && (!search || `${r.name} ${r.itemCode} ${r.brand ?? ''} ${r.category ?? ''}`.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <>
      <PageHeader
        title="Stock"
        subtitle="Materials in the shop, valued at average purchase cost"
        actions={
          <>
            <Button variant="outline" leftIcon="cart" onClick={() => navigate('/purchases/new')}>New purchase</Button>
            <Button leftIcon="layers" onClick={() => navigate('/stock/adjust')}>Adjust stock</Button>
          </>
        }
      />

      {error ? (
        <Card><ErrorState error={error} onRetry={reload} /></Card>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-3">
            <Stat label="Stock value" value={data ? money(data.totalValue) : '…'} />
            <Stat label="Materials" value={data ? data.rows.length : '…'} />
            <Stat label="Low on stock" value={data ? data.lowCount : '…'} tone={data?.lowCount ? 'text-red-600' : undefined} />
          </div>

          <Card className="overflow-hidden">
            <div className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:items-center">
              <Input leftIcon="search" size="sm" type="search" placeholder="Search material" aria-label="Search material" value={search} onChange={(e) => setSearch(e.target.value)} containerClassName="sm:w-72" />
              <Tabs variant="pills" label="Stock filter" value={tab} onChange={setTab} tabs={[{ value: 'all', label: 'All' }, { value: 'low', label: 'Low stock', count: data?.lowCount }]} />
            </div>
            {loading && !data ? (
              <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : rows.length === 0 ? (
              <EmptyState icon="layers" title={tab === 'low' ? 'Nothing is running low' : 'No materials found'} message="Add materials in Materials, then buy them through Purchases." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="border-b border-line text-[13px] text-ink-500">
                    <tr>
                      <th scope="col" className="px-5 py-3 font-normal">Material</th>
                      <th scope="col" className="px-5 py-3 font-normal">Category</th>
                      <th scope="col" className="w-56 px-5 py-3 font-normal">Stock level</th>
                      <th scope="col" className="px-5 py-3 text-right font-normal">Avg cost</th>
                      <th scope="col" className="px-5 py-3 text-right font-normal">Value</th>
                      <th scope="col" className="px-5 py-3"><span className="sr-only">Action</span></th>
                    </tr>
                  </thead>
                  <tbody className="tabular divide-y divide-line">
                    {rows.map((r) => {
                      const pct = r.minStockLevel ? Math.min(100, ((r.currentStock ?? 0) / (r.minStockLevel * 2)) * 100) : 100;
                      return (
                        <tr key={r._id} className="hover:bg-navy-50/60">
                          <td className="px-5 py-3">
                            <Link to={`/materials/${r._id}`} className="font-medium text-navy-900 hover:text-brand-600">{r.name}</Link>
                            <p className="font-mono text-xs text-ink-400">{r.itemCode}</p>
                          </td>
                          <td className="px-5 py-3">{r.category || <span className="text-ink-300">-</span>}</td>
                          <td className="px-5 py-3">
                            <div className="h-1.5 overflow-hidden rounded-full bg-navy-100" aria-hidden="true">
                              <div className={cn('h-full rounded-full', r.isLow ? 'bg-red-500' : 'bg-emerald-500')} style={{ width: `${pct}%` }} />
                            </div>
                            <p className={cn('mt-1 text-xs', r.isLow ? 'text-red-600' : 'text-ink-500')}>
                              {r.currentStock ?? 0} {r.unit} left · min {r.minStockLevel ?? 0}
                            </p>
                          </td>
                          <td className="px-5 py-3 text-right">{money(r.avgPurchaseRate)}</td>
                          <td className="px-5 py-3 text-right font-medium">{money(r.stockValue)}</td>
                          <td className="px-5 py-3 text-right">
                            {r.isLow ? (
                              <Button size="sm" variant="outline" onClick={() => navigate('/purchases/new')}>Reorder</Button>
                            ) : (
                              <Badge tone="green">OK</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <MovementsCard title="Recent stock movements" />
        </div>
      )}
    </>
  );
}
